/**
 * @file 本に載っている答え (別冊の該当ページ) を、紙面の隣の列に出すペインである。
 *
 * @what: 別冊の資料と最初のページを受け取り、そのページの画像を出す。答えが次のページへ続く
 *   ときのために、列の中で別冊の前後のページへ移れる。リーダーのページを送って引ける答えが
 *   変わったら、その答えの最初のページへ戻す。AI は呼ばない。別冊を開いて読む道も下に置く。
 *   answer が null のときは、いまのページから答えを引けないことを1行で出す。
 * @why: 丸付けは答えと自分の解答を見比べながら紙面に書き込む。答えを紙面に重ねると書き込めない
 *   ので、隣に並べて出したままにする (代表の判断、2026-09-16)。
 * @failure: ページの取得に失敗したら、その旨を1行で出す (リーダーはそのまま)。
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { StudySource } from "../api/sources";
import { useScanImage } from "../hooks/useScanImage";
import { useSourcePages } from "../hooks/useSourcePages";
import { printedPageOf } from "../model/sourcePages";
import "./study.css";

export function AnswerPane({
  familyId,
  answer,
  onClose,
}: {
  familyId: string;
  /** 答えが載っている資料 (別冊) と、その答えの最初のページ (リーダーの連番)。引けなければ null。 */
  answer: { source: StudySource; page: number } | null;
  onClose: () => void;
}) {
  return (
    <section className="study-answer-pane" aria-labelledby="study-answer-pane-title">
      <div className="study-answer-pane-head">
        <h3 id="study-answer-pane-title" className="study-note-pane-title">
          答え
        </h3>
        <button
          type="button"
          className="study-note-btn"
          aria-label="答えを閉じる"
          onClick={onClose}
        >
          閉じる
        </button>
      </div>
      {answer === null ? (
        <p className="study-note-muted">このページの答えは別冊から引けません。</p>
      ) : (
        <AnswerPages
          key={`${answer.source.id}:${answer.page}`}
          familyId={familyId}
          source={answer.source}
          startPage={answer.page}
        />
      )}
    </section>
  );
}

function AnswerPages({
  familyId,
  source,
  startPage,
}: {
  familyId: string;
  source: StudySource;
  startPage: number;
}) {
  const state = useSourcePages(familyId, source.id);
  const [page, setPage] = useState(startPage);
  const pages = useMemo(() => (state.status === "ready" ? state.pages : []), [state]);
  const idx = pages.findIndex((p) => p.page === page);
  const target = idx >= 0 ? pages[idx]! : null;
  const image = useScanImage(target?.image_path ?? null, target?.content_type ?? "image/jpeg");
  const printed = printedPageOf(source, page);
  const prev = idx > 0 ? pages[idx - 1]! : null;
  const next = idx >= 0 && idx < pages.length - 1 ? pages[idx + 1]! : null;

  return (
    <>
      <div className="study-answer-pane-nav">
        <button
          type="button"
          className="study-note-btn"
          aria-label="答えの前のページ"
          disabled={prev === null}
          onClick={() => prev && setPage(prev.page)}
        >
          ← 前
        </button>
        <span className="study-answer-pane-where">
          {source.title} p.{printed}
        </span>
        <button
          type="button"
          className="study-note-btn"
          aria-label="答えの次のページ"
          disabled={next === null}
          onClick={() => next && setPage(next.page)}
        >
          次 →
        </button>
      </div>
      <div className="study-answer-pane-body">
        {state.status === "error" ? (
          <p role="alert" className="study-error">
            答えのページを読み込めませんでした。時間を置いて再度お試しください。
          </p>
        ) : state.status !== "ready" ? (
          <p role="status" className="study-note-muted">
            答えのページを探しています...
          </p>
        ) : target === null ? (
          <p className="study-note-muted">
            このページは別冊に取り込まれていません。下から別冊を開いて探してください。
          </p>
        ) : image.status === "loading" ? (
          <p role="status" className="study-note-muted">
            画像を読み込んでいます...
          </p>
        ) : image.status === "error" ? (
          <p role="alert" className="study-error">
            画像を読み込めませんでした。
          </p>
        ) : (
          <img
            src={image.url}
            alt={`${source.title} p.${printed}`}
            className="study-answer-pane-img"
          />
        )}
      </div>
      <p className="study-answer-pane-more">
        <Link
          to={`/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(source.id)}?page=${page}`}
        >
          別冊を開いて読む
        </Link>
      </p>
    </>
  );
}
